import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import Sidebar from "./Sidebar";
import { fetchMenu } from "../../api/newsService";
import { slugify } from "../Sdata";

const INTRODUCTION_SLUG = slugify("Giới thiệu");
const SERVICE_SLUG = slugify("Dịch vụ-Hạ tầng");
const INTRODUCTION_SECTION_SLUGS = new Set([
  slugify("Giới thiệu chung"),
  slugify("Cơ cấu tổ chức"),
  slugify("Hạ tầng mạng lưới"),
]);

function buildMenuTree(data) {
  const map = {};
  const roots = [];

  data.forEach((item) => {
    map[item.id] = { ...item, children: [] };
  });

  data.forEach((item) => {
    if (item.parentId === null || item.parentId === undefined) {
      roots.push(map[item.id]);
      return;
    }

    map[item.parentId]?.children.push(map[item.id]);
  });

  return { roots, map };
}

function findAncestorPath(node, map) {
  const path = [];
  let current = node;

  while (current) {
    path.unshift(current);
    current = current.parentId ? map[current.parentId] : null;
  }

  return path;
}

function SectionMenuItem({ item, currentSlug, ancestorIds, depth = 0 }) {
  const itemSlug = slugify(item.name);
  const hasChildren = item.children?.length > 0;
  const isCurrent = itemSlug === currentSlug;
  const isActiveBranch = ancestorIds.has(item.id);
  const [isOpen, setIsOpen] = useState(hasChildren);

  useEffect(() => {
    if (isActiveBranch) {
      setIsOpen(true);
    }
  }, [isActiveBranch]);

  return (
    <li className="sidebar-section-menu__item">
      {hasChildren ? (
        <>
          <button
            type="button"
            className={`sidebar-section-menu__link sidebar-section-menu__toggle${isCurrent ? " is-current" : ""}${isActiveBranch ? " is-active-branch" : ""}`}
            onClick={() => setIsOpen((prev) => !prev)}
            aria-expanded={isOpen}
          >
            <span>{item.name}</span>
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>

          {isOpen ? (
            <ul
              className={`sidebar-section-menu__children sidebar-section-menu__children--depth-${Math.min(depth + 1, 3)}`}
            >
              {item.children.map((child) => (
                <SectionMenuItem
                  key={child.id}
                  item={child}
                  currentSlug={currentSlug}
                  ancestorIds={ancestorIds}
                  depth={depth + 1}
                />
              ))}
            </ul>
          ) : null}
        </>
      ) : (
        <Link
          to={`/${itemSlug}`}
          className={`sidebar-section-menu__link${isCurrent ? " is-current" : ""}${isActiveBranch ? " is-active-branch" : ""}`}
        >
          <span>{item.name}</span>
        </Link>
      )}
    </li>
  );
}

const CategorySidebar = ({ featuredNewsPage = 1 }) => {
  const { categoryId, Id } = useParams();
  const [menuData, setMenuData] = useState([]);

  useEffect(() => {
    const getMenu = async () => {
      try {
        const res = await fetchMenu();
        if (res?.data?.data) {
          setMenuData(res.data.data);
        }
      } catch (error) {
        console.error("Error fetching menu for contextual sidebar:", error);
      }
    };

    getMenu();
  }, []);

  const introSidebarData = useMemo(() => {
    if (!categoryId || menuData.length === 0) {
      return null;
    }

    const { roots, map } = buildMenuTree(menuData);
    const currentNode = Object.values(map).find((item) => slugify(item.name) === categoryId);
    const introRootFromTree = roots.find((item) => slugify(item.name) === INTRODUCTION_SLUG);
    const introItemsFallback = Object.values(map).filter((item) =>
      INTRODUCTION_SECTION_SLUGS.has(slugify(item.name))
    );

    if (!currentNode) {
      if (!INTRODUCTION_SECTION_SLUGS.has(categoryId)) {
        return null;
      }

      return {
        introRoot: introRootFromTree || {
          id: "intro-root-fallback",
          name: "Giới thiệu",
          children: introItemsFallback,
        },
        ancestorIds: new Set(),
      };
    }

    const path = findAncestorPath(currentNode, map);
    const introRoot = path.find((item) => slugify(item.name) === INTRODUCTION_SLUG) || introRootFromTree;
    const isIntroductionPage =
      !!path.find((item) => slugify(item.name) === INTRODUCTION_SLUG) ||
      INTRODUCTION_SECTION_SLUGS.has(categoryId);

    if (!introRoot || !isIntroductionPage) {
      return null;
    }

    const activePath = findAncestorPath(currentNode, map);

    return {
      introRoot:
        map[introRoot.id] ||
        roots.find((item) => item.id === introRoot.id) || {
          id: "intro-root-fallback",
          name: "Giới thiệu",
          children: introItemsFallback,
        },
      ancestorIds: new Set(activePath.map((item) => item.id)),
    };
  }, [categoryId, menuData]);

  if (categoryId === SERVICE_SLUG) {
    return <Sidebar variant="service" currentServiceId={parseInt(Id, 10)} />;
  }

  if (!introSidebarData?.introRoot?.children?.length) {
    return <Sidebar newsPage={featuredNewsPage} />;
  }

  return (
    <div className="side_bar side_bar--section">
      <section className="sidebar-panel sidebar-panel--section-menu">
        <div className="sidebar-panel__head">
          <div className="sidebar-panel__title-wrap">
            <span className="sidebar-panel__icon">
              <Info size={18} />
            </span>
            <h3>{introSidebarData.introRoot.name}</h3>
          </div>
        </div>

        <div className="sidebar-section-menu">
          <ul className="sidebar-section-menu__list">
            {introSidebarData.introRoot.children.map((item) => (
              <SectionMenuItem
                key={item.id}
                item={item}
                currentSlug={categoryId}
                ancestorIds={introSidebarData.ancestorIds}
                depth={0}
              />
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default CategorySidebar;
